/* eslint-disable react/no-danger */
import {
  Button,
  CardItemProSmall,
  CardItemSlider,
  PressSection,
  ProLandingSectionContent,
  Section,
} from '@presentation/components';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useRouter } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import type { ProLandingPageProps } from '..';
import { STEP_IMAGES, TESTIMONIAL_IMAGES } from './assets';
import agenda from './assets/agenda-0-bg.webp';
import dogPro from './assets/dog-pro.webp';
import { ProSearchForm } from './components/ProSearchForm';
import { TestimonialCardContainer } from './components/TestimonialCardContainer';
import type { TestimonialCardContainerItemData } from './components/TestimonialCardContainer/types';
import * as Styled from './styled';

export * from './types';

export const ProLandingPage: FC<ProLandingPageProps> = (props) => {
  const {
    joinProLink,
    moreVetsLink,
    moreGroomersLink,
    groomerItems = [],
    vetItems = [],
    groomerItemsLoading = false,
    vetItemsLoading = false,
  } = props;
  const { translate, countryCode } = useRouter();

  const loadingCards = useMemo(() => {
    return Array(4)
      .fill(undefined)
      .map((_, index) => <CardItemProSmall style={{ height: 289 }} key={index} loading />);
  }, []);

  const groomerCards = useMemo(() => {
    if (groomerItemsLoading) return loadingCards;
    return groomerItems.map((item, index) => (
      <CardItemProSmall style={{ height: 289 }} key={index} {...item} />
    ));
  }, [groomerItems, groomerItemsLoading, loadingCards]);

  const vetCards = useMemo(() => {
    if (vetItemsLoading) return loadingCards;
    return vetItems.map((item, index) => (
      <CardItemProSmall style={{ height: 289 }} key={index} {...item} />
    ));
  }, [loadingCards, vetItems, vetItemsLoading]);

  const testimonialItems = useMemo((): any[] => {
    return Object.values(translate('proLanding.testimonials.items', { returnObjects: true })).map(
      (item: any, index) =>
        ({
          name: item.name,
          text: item.text,
          image: TESTIMONIAL_IMAGES[index],
        } as TestimonialCardContainerItemData),
    );
  }, [translate]);

  const showLatestProSliders = useMemo(() => {
    return countryCode === CountryCode.BE;
  }, [countryCode]);

  return (
    <>
      <Styled.TopSection>
        <Styled.TopDecoration src={dogPro} />
        <Styled.TopContainer>
          <Styled.TopContainerContent>
            <Styled.TopContainerTitle
              dangerouslySetInnerHTML={{ __html: translate('proLanding.title') }}
            />
            <Styled.TopSearchCard>
              <ProSearchForm {...props} />
              <Styled.TopSearchCardLink to={joinProLink}>
                {translate('common.buttons.joinAsPro')}
              </Styled.TopSearchCardLink>
            </Styled.TopSearchCard>
          </Styled.TopContainerContent>
        </Styled.TopContainer>
      </Styled.TopSection>

      <Styled.SliderContainer>
        <Section centerChildren>
          <Styled.StepsTitle
            dangerouslySetInnerHTML={{ __html: translate('proLanding.stepsSection.title') }}
          />
          <Styled.StepsItemsContainer>
            {STEP_IMAGES.map((stepImage, index) => {
              return (
                <Styled.StepsItemCard key={index}>
                  <Styled.StepsItemCardImg src={stepImage} />
                  <Styled.StepsItemCardText>
                    {translate(`proLanding.stepsSection.items.${index + 1}.text`)}
                  </Styled.StepsItemCardText>
                </Styled.StepsItemCard>
              );
            })}
          </Styled.StepsItemsContainer>
        </Section>
      </Styled.SliderContainer>

      {showLatestProSliders ? (
        <>
          <Styled.SliderContainer>
            <Section centerChildren>
              <Styled.BlueSectionTitle
                dangerouslySetInnerHTML={{ __html: translate('proLanding.vetsSliderTitle') }}
              />
              <CardItemSlider items={vetCards} />
              <Button color="blue" shrink outlined>
                <Link to={moreVetsLink}>{translate('common.buttons.seeMore')}</Link>
              </Button>
            </Section>
          </Styled.SliderContainer>

          <Styled.SliderContainer>
            <Section centerChildren>
              <Styled.BlueSectionTitle
                dangerouslySetInnerHTML={{ __html: translate('proLanding.groomersSliderTitle') }}
              />
              <CardItemSlider items={groomerCards} />
              <Button color="blue" shrink outlined>
                <Link to={moreGroomersLink}>{translate('common.buttons.seeMore')}</Link>
              </Button>
            </Section>
          </Styled.SliderContainer>
        </>
      ) : null}
      <Styled.TestimonialsWrapper>
        <Section centerChildren>
          <Styled.BlueSectionTitle
            dangerouslySetInnerHTML={{ __html: translate('proLanding.testimonials.title') }}
          />
          <TestimonialCardContainer items={testimonialItems} />
        </Section>
      </Styled.TestimonialsWrapper>

      <ProLandingSectionContent
        title={translate('common.sections.areYouPro.title')}
        contents={[
          {
            subtitle: translate('common.sections.areYouPro.subtitle1'),
            text: translate('common.sections.areYouPro.text1'),
          },
          {
            subtitle: translate('common.sections.areYouPro.subtitle2'),
            text: translate('common.sections.areYouPro.text2'),
          },
        ]}
        image={agenda}
      >
        <Link to={joinProLink}>
          <Button color="blue" shrink outlined>
            {translate('common.buttons.seeMore')}
          </Button>
        </Link>
      </ProLandingSectionContent>

      <PressSection />
    </>
  );
};
